import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

const CARD_CONFIGS = {
  club: [
    { key: 'male', title: 'Male Leaders', gender: 'MALE', customerType: '' },
    { key: 'female', title: 'Female Leaders', gender: 'FEMALE', customerType: '' },
    { key: 'onlineMale', title: 'Online Male Leaders', gender: 'MALE', customerType: 'ONLINE' },
    { key: 'clubMale', title: 'Club Male Leaders', gender: 'MALE', customerType: 'CLUB' },
    { key: 'onlineFemale', title: 'Online Female Leaders', gender: 'FEMALE', customerType: 'ONLINE' },
    { key: 'clubFemale', title: 'Club Female Leaders', gender: 'FEMALE', customerType: 'CLUB' }
  ],
  org: [
    { key: 'male', title: 'Male Leaders', gender: 'MALE', customerType: '' },
    { key: 'female', title: 'Female Leaders', gender: 'FEMALE', customerType: '' },
    { key: 'onlineMale', title: 'Online Male Leaders', gender: 'MALE', customerType: 'ONLINE' },
    { key: 'clubMale', title: 'Club Male Leaders', gender: 'MALE', customerType: 'CLUB' },
    { key: 'onlineFemale', title: 'Online Female Leaders', gender: 'FEMALE', customerType: 'ONLINE' },
    { key: 'clubFemale', title: 'Club Female Leaders', gender: 'FEMALE', customerType: 'CLUB' }
  ]
};

const createInitialCardState = (cards) => (
  cards.reduce((acc, card) => {
    acc[card.key] = { loading: true, error: '', data: null };
    return acc;
  }, {})
);

const formatWeight = (value) => (value === null || value === undefined ? '-' : `${Number(value).toFixed(2)} kg`);
const formatPercentage = (value) => (value === null || value === undefined ? '-' : `${Number(value).toFixed(2)}%`);
const getInitials = (name) => (
  (name || '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || '')
    .join('') || '?'
);

const LeaderboardDrilldownModal = ({ open, title, state, onClose, showClubName, rankingMetric, t }) => {
  const showPercentageStats = rankingMetric !== 'WEIGHT_LOSS';

  if (!open) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'white',
          width: 'min(1120px, 94vw)',
          maxHeight: '88vh',
          overflow: 'auto',
          borderRadius: '16px',
          padding: '24px',
          boxShadow: '0 20px 60px rgba(44, 82, 130, 0.18)'
        }}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="card" style={{ padding: '24px', marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '240px' }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '6px 12px', background: 'var(--pale-blue)', color: 'var(--dark-blue)', borderRadius: '999px', fontSize: '12px', fontWeight: 700, marginBottom: '14px' }}>
                {t('Challenge')}
              </div>
              <h1 className="page-title" style={{ marginBottom: '8px' }}>{title}</h1>
              <p style={{ color: 'var(--gray-dark)', margin: 0, lineHeight: 1.6 }}>
                {t('Full leaderboard list ranked by {metric} for this category.', {
                  metric: rankingMetric === 'WEIGHT_LOSS' ? t('weight loss') : t('percentage of weight loss')
                })}
              </p>
            </div>
            <button type="button" className="btn btn-secondary" onClick={onClose}>{t('Close')}</button>
          </div>

          {!state.loading && !state.error && !!state.data?.entries?.length && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '14px', marginTop: '20px' }}>
              <div style={{ background: 'var(--gray-light)', border: '1px solid var(--gray-medium)', borderRadius: '10px', padding: '16px' }}>
                <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--gray-dark)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '6px' }}>
                  {t('Participants')}
                </div>
                <div style={{ fontSize: '28px', fontWeight: 700, color: 'var(--dark-blue)' }}>
                  {state.data.entries.length}
                </div>
              </div>
              <div style={{ background: 'var(--gray-light)', border: '1px solid var(--gray-medium)', borderRadius: '10px', padding: '16px' }}>
                <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--gray-dark)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '6px' }}>
                  {t('Best Weight Loss')}
                </div>
                <div style={{ fontSize: '28px', fontWeight: 700, color: 'var(--dark-blue)' }}>
                  {formatWeight(state.data.entries[0]?.weightLoss)}
                </div>
              </div>
              {showPercentageStats && (
                <div style={{ background: 'var(--gray-light)', border: '1px solid var(--gray-medium)', borderRadius: '10px', padding: '16px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--gray-dark)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '6px' }}>
                    {t('Best % Loss')}
                  </div>
                  <div style={{ fontSize: '28px', fontWeight: 700, color: 'var(--dark-blue)' }}>
                    {formatPercentage(state.data.entries[0]?.weightLossPercentage)}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {state.loading ? (
          <div className="loading">{t('Loading members...')}</div>
        ) : state.error ? (
          <div className="alert alert-error">{state.error}</div>
        ) : !state.data?.entries?.length ? (
          <div className="card">
            <div style={{ textAlign: 'center', padding: '42px 20px', color: 'var(--gray-dark)', background: 'var(--gray-light)', borderRadius: '10px', border: '1px dashed var(--gray-medium)' }}>
              {t('No members found for this category.')}
            </div>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '18px' }}>
            <LeaderboardPodium entries={state.data.entries} showClubName={showClubName} t={t} />
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap', marginBottom: '18px' }}>
                <div>
                  <h3 style={{ color: 'var(--dark-blue)', margin: '0 0 6px 0' }}>{t('Participants')}</h3>
                  <p style={{ margin: 0, color: 'var(--gray-dark)', fontSize: '14px' }}>
                    {t('Latest three weight entries are shown as week1, week2, and week3.')}
                  </p>
                </div>
                <div style={{ fontSize: '13px', color: 'var(--gray-dark)', background: 'var(--gray-light)', border: '1px solid var(--gray-medium)', borderRadius: '999px', padding: '8px 12px' }}>
                  {t('Total')}: {state.data.entries.length}
                </div>
              </div>
              <div style={{ overflowX: 'auto', border: '1px solid var(--gray-medium)', borderRadius: '12px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px', minWidth: showClubName ? '1180px' : '1040px' }}>
                  <thead>
                    <tr style={{ backgroundColor: 'var(--pale-blue)' }}>
                      <th style={{ padding: '14px 16px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Rank')}</th>
                      <th style={{ padding: '14px 16px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Participant')}</th>
                      {showClubName && <th style={{ padding: '14px 16px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Club')}</th>}
                      <th style={{ padding: '14px 16px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Gender')}</th>
                      <th style={{ padding: '14px 16px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Type')}</th>
                      <th style={{ padding: '14px 16px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Start Weight')}</th>
                      <th style={{ padding: '14px 16px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('End Weight')}</th>
                      <th style={{ padding: '14px 16px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Weight Loss')}</th>
                    {showPercentageStats && <th style={{ padding: '14px 16px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('% Loss')}</th>}
                    <th style={{ padding: '14px 16px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Week1')}</th>
                    <th style={{ padding: '14px 16px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Week2')}</th>
                    <th style={{ padding: '14px 16px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Week3')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {state.data.entries.map((entry, index) => (
                      <tr
                        key={`${entry.customerId}-${index}`}
                        style={{
                          backgroundColor: index % 2 === 0 ? 'var(--white)' : 'var(--gray-light)',
                          borderBottom: '1px solid var(--gray-medium)'
                        }}
                      >
                        <td style={{ padding: '14px 16px', fontWeight: 700, color: 'var(--dark-blue)' }}>{index + 1}</td>
                        <td style={{ padding: '14px 16px' }}>
                          <div style={{ fontWeight: 600, color: 'var(--dark-blue)', marginBottom: '4px' }}>
                            {entry.fullName}
                          </div>
                          <div style={{ fontSize: '12px', color: 'var(--gray-dark)' }}>
                            {entry.firstReadingDate} {t('to')} {entry.latestReadingDate}
                          </div>
                        </td>
                        {showClubName && (
                          <td style={{ padding: '14px 16px', color: 'var(--gray-dark)' }}>
                            {entry.clubName || t('-')}
                          </td>
                        )}
                        <td style={{ padding: '14px 16px', color: 'var(--dark-blue)', fontWeight: 500 }}>
                          {entry.gender ? t(entry.gender) : t('-')}
                        </td>
                        <td style={{ padding: '14px 16px' }}>
                          <span
                            style={{
                              background: entry.customerType === 'ONLINE' ? 'var(--primary-blue)' : 'var(--success)',
                              color: 'white',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: 600
                            }}
                          >
                            {entry.customerType ? t(entry.customerType) : t('N/A')}
                          </span>
                        </td>
                        <td style={{ padding: '14px 16px', color: 'var(--gray-dark)' }}>{formatWeight(entry.startWeight)}</td>
                        <td style={{ padding: '14px 16px', color: 'var(--gray-dark)' }}>{formatWeight(entry.endWeight)}</td>
                        <td style={{ padding: '14px 16px', color: 'var(--gray-dark)' }}>{formatWeight(entry.weightLoss)}</td>
                      {showPercentageStats && (
                        <td style={{ padding: '14px 16px' }}>
                          <span style={{ color: 'var(--success)', fontWeight: 700 }}>
                            {formatPercentage(entry.weightLossPercentage)}
                          </span>
                        </td>
                      )}
                      <td style={{ padding: '14px 16px', color: 'var(--gray-dark)' }}>{formatWeight(entry.week1)}</td>
                      <td style={{ padding: '14px 16px', color: 'var(--gray-dark)' }}>{formatWeight(entry.week2)}</td>
                      <td style={{ padding: '14px 16px', color: 'var(--gray-dark)' }}>{formatWeight(entry.week3)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const LeaderboardPodium = ({ entries, showClubName, t }) => {
  const topThree = entries.slice(0, 3);
  const displayOrder = [1, 0, 2]
    .map((index) => ({
      entry: topThree[index],
      actualRank: index + 1
    }))
    .filter((item) => item.entry)
    ;

  const podiumConfigByRank = {
    1: {
      rank: 1,
      medal: '🏆',
      gradient: 'linear-gradient(180deg, #FFF7D6 0%, #F6D365 100%)',
      accent: '#D4A017',
      order: 2,
      minHeight: '228px',
      transform: 'translateY(0)'
    },
    2: {
      rank: 2,
      medal: '🥈',
      gradient: 'linear-gradient(180deg, #F8FAFC 0%, #E2E8F0 100%)',
      accent: '#94A3B8',
      order: 1,
      minHeight: '190px',
      transform: 'translateY(24px)'
    },
    3: {
      rank: 3,
      medal: '🥉',
      gradient: 'linear-gradient(180deg, #FBE9E7 0%, #D1916B 100%)',
      accent: '#B76E3A',
      order: 3,
      minHeight: '176px',
      transform: 'translateY(34px)'
    }
  };

  return (
    <div
      style={{
        borderRadius: '18px',
        padding: '18px',
        background: 'linear-gradient(180deg, rgba(235, 248, 255, 0.95) 0%, rgba(255, 255, 255, 1) 100%)',
        border: '1px solid rgba(66, 153, 225, 0.18)',
        boxShadow: '0 18px 40px rgba(44, 82, 130, 0.08)',
        overflow: 'hidden'
      }}
    >
      <div style={{ marginBottom: '16px' }}>
        <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--primary-blue)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px' }}>
          {t('Top 3 Transformation Leaders')}
        </div>
        <div style={{ color: 'var(--gray-dark)', fontSize: '14px' }}>
          {t('Premium podium view for the strongest weight-loss results in this challenge category.')}
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: '14px',
          alignItems: 'end'
        }}
      >
        {displayOrder.map(({ entry, actualRank }) => {
          const config = podiumConfigByRank[actualRank];
          const isWinner = config.rank === 1;

          return (
            <div
              key={`podium-${entry.customerId}`}
              className={isWinner ? 'leaderboard-podium-glow' : ''}
              style={{
                order: config.order,
                minHeight: config.minHeight,
                transform: config.transform,
                borderRadius: '20px',
                padding: '18px 16px',
                background: config.gradient,
                border: `1px solid ${config.accent}55`,
                boxShadow: isWinner
                  ? '0 18px 40px rgba(212, 160, 23, 0.28)'
                  : '0 14px 30px rgba(44, 82, 130, 0.10)',
                position: 'relative',
                textAlign: 'center'
              }}
            >
              <div style={{ fontSize: isWinner ? '28px' : '24px', marginBottom: '10px' }}>{config.medal}</div>
              <div
                style={{
                  width: isWinner ? '72px' : '60px',
                  height: isWinner ? '72px' : '60px',
                  borderRadius: '999px',
                  margin: '0 auto 12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 800,
                  fontSize: isWinner ? '22px' : '18px',
                  color: 'var(--dark-blue)',
                  background: 'rgba(255,255,255,0.75)',
                  border: '2px solid rgba(255,255,255,0.75)'
                }}
              >
                {getInitials(entry.fullName)}
              </div>
              <div style={{ fontSize: '12px', fontWeight: 700, color: config.accent, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px' }}>
                {t('Rank #{rank}', { rank: config.rank })}
              </div>
              <div style={{ color: 'var(--dark-blue)', fontWeight: 700, fontSize: isWinner ? '17px' : '15px', lineHeight: 1.35 }}>
                {entry.fullName}
              </div>
              <div style={{ marginTop: '10px', color: 'var(--dark-blue)', fontWeight: 800, fontSize: isWinner ? '18px' : '16px' }}>
                ⬇ {Number(entry.weightLoss || 0).toFixed(2)} kg
              </div>
              <div style={{ marginTop: '6px', color: 'var(--gray-dark)', fontSize: '13px', fontWeight: 600 }}>
                {formatPercentage(entry.weightLossPercentage)}
              </div>
              {showClubName && entry.clubName && (
                <div style={{ marginTop: '8px', color: 'var(--gray-dark)', fontSize: '12px' }}>
                  {entry.clubName}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const LeaderboardCard = ({ title, state, showClubName, onDrillDown, enableDrillDown, rankingMetric, t }) => {
  const showPercentageStats = rankingMetric !== 'WEIGHT_LOSS';
  const entries = state.data?.entries || [];

  return (
    <div className="card" style={{ minHeight: '280px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'flex-start', marginBottom: '14px' }}>
        <h3 style={{ color: 'var(--dark-blue)', margin: 0 }}>{title}</h3>
        {enableDrillDown && (
          <button
            type="button"
            onClick={onDrillDown}
            style={{
              border: 'none',
              background: 'none',
              color: 'var(--primary-blue)',
              fontWeight: 600,
              cursor: 'pointer',
              padding: 0
            }}
          >
            {t('Drill down')}
          </button>
        )}
      </div>
      {state.loading ? (
        <div className="loading">{t('Loading leaders...')}</div>
      ) : state.error ? (
        <div className="alert alert-error">{state.error}</div>
      ) : !entries.length ? (
        <div style={{ color: 'var(--gray-dark)' }}>{t('No qualifying customers found for this range.')}</div>
      ) : (
        <div style={{ display: 'grid', gap: '10px' }}>
          {entries.map((entry, index) => (
            <div key={`${entry.customerId}-${index + 1}`} style={{ border: '1px solid var(--gray-medium)', borderRadius: '10px', padding: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', alignItems: 'baseline' }}>
                <strong>{index + 1}. {entry.fullName}</strong>
                <span style={{ color: 'var(--success)', fontWeight: 700 }}>
                  {rankingMetric === 'WEIGHT_LOSS'
                    ? formatWeight(entry.weightLoss)
                    : formatPercentage(entry.weightLossPercentage)}
                </span>
              </div>
              {showPercentageStats && (
                <div style={{ fontSize: '13px', color: 'var(--gray-dark)', marginTop: '4px' }}>
                  {t('Loss')}: {formatWeight(entry.weightLoss)}
                </div>
              )}
              <div style={{ fontSize: '13px', color: 'var(--gray-dark)', marginTop: '4px' }}>
                {(entry.gender ? t(entry.gender) : t('ALL'))} • {(entry.customerType ? t(entry.customerType) : t('ALL'))}
              </div>
              {showClubName && entry.clubName && (
                <div style={{ fontSize: '13px', color: 'var(--dark-blue)', marginTop: '4px', fontWeight: 500 }}>
                  {t('Club')}: {entry.clubName}
                </div>
              )}
              <div style={{ fontSize: '13px', color: 'var(--gray-dark)', marginTop: '4px' }}>
                {formatWeight(entry.startWeight)} → {formatWeight(entry.endWeight)}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--gray-dark)', marginTop: '4px' }}>
                {entry.firstReadingDate} {t('to')} {entry.latestReadingDate}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const LeaderboardSection = ({ scope, startDate, endDate, contestId, enableDrillDown = false, rankingMetric = 'PERCENTAGE', t }) => {
  const cards = useMemo(
    () => CARD_CONFIGS[scope === 'contestClub' || scope === 'contestOrg' ? 'club' : scope],
    [scope]
  );
  const [cardStates, setCardStates] = useState(() => createInitialCardState(cards));
  const [drilldownState, setDrilldownState] = useState({ open: false, card: null, loading: false, error: '', data: null });
  const showClubName = scope === 'org' || scope === 'contestOrg';

  useEffect(() => {
    if (!startDate || !endDate || ((scope === 'contestClub' || scope === 'contestOrg') && !contestId)) {
      setCardStates(createInitialCardState(cards));
      setDrilldownState({ open: false, card: null, loading: false, error: '', data: null });
      return;
    }

    setCardStates(createInitialCardState(cards));
    setDrilldownState({ open: false, card: null, loading: false, error: '', data: null });
    cards.forEach((card) => {
      const params = {
        scope,
        startDate,
        endDate,
        gender: card.gender || undefined,
        customerType: card.customerType || undefined,
        contestId: contestId || undefined,
        rankingMetric
      };

      axios.get('/api/leaderboard/rankings', { params })
        .then((response) => {
          setCardStates((current) => ({
            ...current,
            [card.key]: { loading: false, error: '', data: response.data }
          }));
        })
        .catch((error) => {
          console.error(`Failed to load ${card.key} leaderboard:`, error);
          setCardStates((current) => ({
            ...current,
            [card.key]: { loading: false, error: t('Failed to load leaderboard.'), data: null }
          }));
        });
    });
  }, [cards, scope, startDate, endDate, contestId, rankingMetric, t]);

  const openDrilldown = (card) => {
    setDrilldownState({ open: true, card, loading: true, error: '', data: null });

    const params = {
      scope,
      startDate,
      endDate,
      gender: card.gender || undefined,
      customerType: card.customerType || undefined,
      contestId: contestId || undefined,
      fullResults: true,
      rankingMetric
    };

    axios.get('/api/leaderboard/rankings', { params })
      .then((response) => {
        setDrilldownState({ open: true, card, loading: false, error: '', data: response.data });
      })
      .catch((error) => {
        console.error(`Failed to load ${card.key} drilldown leaderboard:`, error);
        setDrilldownState({ open: true, card, loading: false, error: t('Failed to load members.'), data: null });
      });
  };

  const closeDrilldown = () => {
    setDrilldownState((current) => ({ ...current, open: false }));
  };

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '16px' }}>
        {cards.map((card) => (
          <LeaderboardCard
            key={card.key}
            title={t(card.title)}
            state={cardStates[card.key] || { loading: true, error: '', data: null }}
            showClubName={showClubName}
            onDrillDown={() => openDrilldown(card)}
            enableDrillDown={enableDrillDown}
            rankingMetric={rankingMetric}
            t={t}
          />
        ))}
      </div>
      {enableDrillDown && (
        <LeaderboardDrilldownModal
          open={drilldownState.open}
          title={drilldownState.card ? t(drilldownState.card.title) : t('Leaderboard Details')}
          state={drilldownState}
          onClose={closeDrilldown}
          showClubName={showClubName}
          rankingMetric={rankingMetric}
          t={t}
        />
      )}
    </>
  );
};

const Leaderboard = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const monthAgo = useMemo(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date.toISOString().slice(0, 10);
  }, []);
  const isSystemCustomer = user?.systemRoles?.some((role) => role.code === 'SYSTEM_CUSTOMER');
  const leaderboardRankingMetric = isSystemCustomer ? 'WEIGHT_LOSS' : 'PERCENTAGE';

  const [activeTab, setActiveTab] = useState(isSystemCustomer ? 'contest' : 'club');
  const [dateRange, setDateRange] = useState({ startDate: monthAgo, endDate: today });
  const [contests, setContests] = useState([]);
  const [selectedContestId, setSelectedContestId] = useState('');
  const [contestLoading, setContestLoading] = useState(true);
  const [contestError, setContestError] = useState('');

  useEffect(() => {
    if (isSystemCustomer && activeTab !== 'contest') {
      setActiveTab('contest');
    }
  }, [activeTab, isSystemCustomer]);

  useEffect(() => {
    axios.get('/api/contests')
      .then((response) => {
        setContests(response.data || []);
        if (response.data?.length) {
          setSelectedContestId(String(response.data[0].id));
        }
      })
      .catch((error) => {
        console.error('Failed to load contests:', error);
        setContestError(t('Failed to load challenges.'));
      })
      .finally(() => setContestLoading(false));
  }, [t]);

  const selectedContest = contests.find((contest) => String(contest.id) === String(selectedContestId));

  return (
    <div className="page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap', marginBottom: '20px' }}>
        <div>
          <h1 className="page-title" style={{ marginBottom: '8px' }}>{t('Leaderboard')}</h1>
          <p style={{ color: 'var(--gray-dark)', margin: 0 }}>
            {isSystemCustomer
              ? t('Track top customer weight loss in the challenge leaderboard.')
              : t('Track top customer weight loss across club, org, and challenge views.')}
          </p>
        </div>
        <Link to="/dashboard" className="btn btn-secondary">{t('Back to Dashboard')}</Link>
      </div>

      <div className="card mb-20">
        {!isSystemCustomer && (
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '20px' }}>
            {[
              { key: 'club', label: 'Club Level' },
              { key: 'org', label: 'Org Level' },
              { key: 'contest', label: 'Challenge Level' }
            ].map((tab) => (
              <button
                key={tab.key}
                className={activeTab === tab.key ? 'btn btn-primary' : 'btn btn-secondary'}
                onClick={() => setActiveTab(tab.key)}
              >
                {t(tab.label)}
              </button>
            ))}
          </div>
        )}

        {activeTab !== 'contest' ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
            <div>
              <label className="form-label">{t('Start Date')}</label>
              <input type="date" className="form-input" value={dateRange.startDate} onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })} />
            </div>
            <div>
              <label className="form-label">{t('End Date')}</label>
              <input type="date" className="form-input" value={dateRange.endDate} onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })} />
            </div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
            <div>
              <label className="form-label">{t('Challenge')}</label>
              <select className="form-input" value={selectedContestId} onChange={(e) => setSelectedContestId(e.target.value)}>
                {contests.map((contest) => (
                  <option key={contest.id} value={contest.id}>
                    {contest.name} ({contest.startDate} {t('to')} {contest.endDate})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">{t('Challenge Date Range')}</label>
              <div className="form-input" style={{ display: 'flex', alignItems: 'center', background: 'var(--gray-light)' }}>
                {selectedContest ? `${selectedContest.startDate} ${t('to')} ${selectedContest.endDate}` : t('Select a challenge')}
              </div>
            </div>
          </div>
        )}
      </div>

      {activeTab === 'club' && (
        <LeaderboardSection scope="club" startDate={dateRange.startDate} endDate={dateRange.endDate} t={t} />
      )}

      {activeTab === 'org' && (
        <LeaderboardSection scope="org" startDate={dateRange.startDate} endDate={dateRange.endDate} t={t} />
      )}

      {activeTab === 'contest' && (
        contestLoading ? (
          <div className="loading">{t('Loading challenges...')}</div>
        ) : contestError ? (
          <div className="alert alert-error">{contestError}</div>
        ) : !selectedContest ? (
          <div className="card">{t('No challenges available.')}</div>
        ) : (
          <div style={{ display: 'grid', gap: '20px' }}>
            <div>
              <h2 style={{ color: 'var(--dark-blue)', marginBottom: '12px' }}>{t('Club Level Leaders')}</h2>
              <LeaderboardSection
                scope="contestClub"
                contestId={selectedContest.id}
                startDate={selectedContest.startDate}
                endDate={selectedContest.endDate}
                enableDrillDown={!isSystemCustomer}
                rankingMetric={leaderboardRankingMetric}
                t={t}
              />
            </div>
            <div>
              <h2 style={{ color: 'var(--dark-blue)', marginBottom: '12px' }}>{t('Org Level Leaders')}</h2>
              <LeaderboardSection
                scope="contestOrg"
                contestId={selectedContest.id}
                startDate={selectedContest.startDate}
                endDate={selectedContest.endDate}
                rankingMetric={leaderboardRankingMetric}
                t={t}
              />
            </div>
          </div>
        )
      )}
    </div>
  );
};

export default Leaderboard;
